import { getCalculatedDeliveryDates } from '@/clients/pops/client'
import type {
  DeliveryDataState,
  GroupedAddonsWithQuanity,
} from '@/components/FlexibleProgramPanel/Form'
import { NOTICE_ADD_TO_CART, SHOW_CART_MODAL } from '@/constants/cart-events'
import { MEAL_PROGRAM_SELLING_PLAN_ID } from '@/constants/commerce'
import { PurchaseTypes } from '@/constants/programs'
import type { SelectedOptionsState } from '@/hooks/useFlexibleProgram'
import type { Delivery, DeliveryZone } from '@/models/delivery-zone'
import { addItems } from '@/scripts/cart'
import { getDeliveryTimeString } from '@/scripts/program-panel/delivery-window'
import { chord } from '@/scripts/tracking/chord'
import { transformToChordItem } from '@/scripts/tracking/helper'
import { $customer } from '@/stores/account/stores'
import { openDialog } from '@/stores/dialog/actions'
import { transformObjectToCartItemAttributes } from '@/utilities/commerce'
import type { Dayjs } from '@/utilities/dayjs-config'
import { captureException } from '@/utilities/logger-fe'
import { formatPrice } from '@/utilities/number'
import { formatMeals, getWeekOfDate } from '@/utilities/sakara'

export type FlexibleProgramDeliveryProperties = {
  _zip_code: string
  _start_date: string
  _delivery_zone: string
  _delivery_zone_id: string
  _delivery_days_text: string
  _delivery_window: string
  _delivery_window_id: string
  _delivery_window_text: string
  _number_of_deliveries: string
  _program_delivery_dates: string
  _kitchen: string
}

export type FlexibleProgramBaseProperties = {
  _days_of_food: string
  _meals: string
  _supplement: string
  _display_line_properties: string
  _product_path: string
  _default_sku: string
  _sku_week_2?: string
  _subscription_week: string
  _addons?: string
  _addon_groups?: string
}

export type FlexibleProgramRedemptionProperties = {
  _yotpo_coupon_code: string
  _swell_points_used: string
  _swell_redemption_id: string
  _swell_redemption_token: string
  _swell_discount_type: string
  _swell_discount_amount: string
  _swell_partial_redemption: string
}

export type FlexibleProgramProperties = FlexibleProgramBaseProperties &
  Partial<FlexibleProgramDeliveryProperties> &
  Partial<FlexibleProgramRedemptionProperties>

const getLineProperties = ({
  selectedOptions,
  selectedOptionsBeforeDowngrade,
}: {
  selectedOptions: SelectedOptionsState
  selectedOptionsBeforeDowngrade?: SelectedOptionsState
}) => {
  const properties = []
  const { meals, daysOption, daysAndMealsOption } = selectedOptions
  if (meals) {
    properties.push(formatMeals(meals, ', '))
  }
  if (daysAndMealsOption) {
    properties.push(daysAndMealsOption.label)
  } else if (daysOption) {
    properties.push(daysOption.label)
  }

  if (selectedOptionsBeforeDowngrade) {
    const { days, activeVariant: { price } = {} } =
      selectedOptionsBeforeDowngrade

    if (days && price) {
      properties.push(`
        After the shortened holiday week, your program will be adjusted to ${days} days a week, ${formatPrice(
          price,
        )} weekly
      `)
    }
  }

  return JSON.stringify(properties)
}

const getProgramProperties = async ({
  selectedOptions,
  groupedAddonsWithQuantity,
  deliveryData,
  schedulePowWeek,
  redemptionProperties,
  selectedOptionsBeforeDowngrade,
}: {
  selectedOptions: SelectedOptionsState
  groupedAddonsWithQuantity: GroupedAddonsWithQuanity
  deliveryData?: DeliveryDataState
  schedulePowWeek?: boolean
  redemptionProperties?: FlexibleProgramRedemptionProperties
  selectedOptionsBeforeDowngrade?: SelectedOptionsState
}): Promise<FlexibleProgramProperties> => {
  const baseProperties: FlexibleProgramBaseProperties = {
    _days_of_food: JSON.stringify(selectedOptions.days),
    _meals: JSON.stringify(selectedOptions.meals),
    _supplement: 'probiotics',
    _display_line_properties: getLineProperties({
      selectedOptions,
      selectedOptionsBeforeDowngrade,
    }),
    _product_path: window.location.pathname,
    _default_sku: selectedOptions.activeVariant!.sku.toString(),
    ...(selectedOptionsBeforeDowngrade && {
      _sku_week_2: selectedOptionsBeforeDowngrade.activeVariant!.sku,
    }),
    _subscription_week:
      selectedOptions.purchaseType === PurchaseTypes.SUBSCRIPTION ? '1' : '0',
    ...(selectedOptions.purchaseType === PurchaseTypes.PACK_OF_WEEKS && {
      _number_of_weeks: selectedOptions.weeks?.toString(),
      _redemption_number: 'P-0',
    }),
    ...(Object.values(groupedAddonsWithQuantity).flatMap(({ addons }) => addons)
      .length && {
      _addons: JSON.stringify(
        Object.values(groupedAddonsWithQuantity).flatMap(({ addons }) =>
          addons.map(({ addon, quantity }) => ({
            id: addon.variant.id,
            quantity,
          })),
        ),
      ),
    }),
    ...(Object.values(groupedAddonsWithQuantity).flatMap(({ addons }) => addons)
      .length && {
      _addon_groups: JSON.stringify(
        Object.values(groupedAddonsWithQuantity).map(({ group }) => ({
          id: group._id,
          label: group.label,
          addonIds: group.options.map((option) => option.variant.id),
          validation: { max: group.optionGroupMaxCount },
        })),
      ),
    }),
  }

  const deliveryProperties =
    deliveryData &&
    deliveryData.zipCode &&
    deliveryData.startDate &&
    deliveryData.deliveryZone &&
    selectedOptions.activeVariant &&
    (await getDeliveryProperties({
      zipCode: deliveryData.zipCode,
      startDate: deliveryData.startDate,
      deliveryZone: deliveryData.deliveryZone,
      deliveries: deliveryData.deliveries,
      sku: selectedOptions.activeVariant.sku,
      purchaseType: selectedOptions.purchaseType,
      schedulePowWeek: schedulePowWeek,
    }))

  return {
    ...baseProperties,
    ...(deliveryProperties ? deliveryProperties : {}),
    ...(redemptionProperties ? redemptionProperties : {}),
  }
}

const getDeliveryProperties = async ({
  zipCode,
  startDate,
  deliveryZone,
  deliveries,
  sku,
  purchaseType,
  schedulePowWeek,
}: {
  zipCode: string
  startDate: Dayjs
  deliveryZone: DeliveryZone
  deliveries: Delivery[]
  sku: string
  purchaseType?: string
  schedulePowWeek?: boolean
}) => {
  if (purchaseType === PurchaseTypes.PACK_OF_WEEKS) {
    if (schedulePowWeek) {
      return {
        _zip_code: zipCode,
        _start_date: startDate.format('YYYY-MM-DD'),
      }
    } else {
      return {}
    }
  }

  const deliveryDates = await getCalculatedDeliveryDates(
    getWeekOfDate(startDate.format('YYYY-MM-DD')),
    startDate.format('YYYY-MM-DD'),
    deliveryZone.id.toString(),
    deliveryZone.defaultDeliveryWindowId.toString(),
    sku,
  )
  const deliveryProperties = {
    _zip_code: zipCode,
    _start_date: startDate.format('YYYY-MM-DD'),
    _delivery_zone: deliveryZone.name,
    _delivery_zone_id: deliveryZone.id.toString(),
    _delivery_days_text: deliveries
      .map((delivery) => delivery.day)
      .join(' and '),
    _delivery_window: deliveryZone.defaultDeliveryWindow.join('-'),
    _delivery_window_id: deliveryZone.defaultDeliveryWindowId.toString(),
    _delivery_window_text: getDeliveryTimeString(
      deliveryZone.defaultDeliveryWindow,
    ),
    _number_of_deliveries: deliveries.length.toString(),
    _program_delivery_dates: JSON.stringify(deliveryDates),
    _kitchen: deliveryZone.kitchen,
  }

  return deliveryProperties
}

export const addProgramToCart = async ({
  selectedOptions,
  groupedAddonsWithQuantity,
  deliveryData,
  schedulePowWeek,
  amountToRedeem,
  sectionId,
  sectionType,
  selectedOptionsBeforeDowngrade,
  isInfoModalEnabled,
}: {
  selectedOptions: SelectedOptionsState
  groupedAddonsWithQuantity: GroupedAddonsWithQuanity
  deliveryData?: DeliveryDataState
  schedulePowWeek?: boolean
  amountToRedeem: number
  sectionId: string
  sectionType: string
  selectedOptionsBeforeDowngrade?: SelectedOptionsState
  isInfoModalEnabled: boolean
}) => {
  if (!selectedOptions.activeVariant) return

  let redemptionProperties: FlexibleProgramRedemptionProperties | undefined
  if (amountToRedeem > 0) {
    try {
      const response = await fetch('/api/yotpo-redemption', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redemption_option_id: 869132,
          customer_email: $customer.get()?.email,
          delay_points_deduction: true,
          points_to_redeem: 1250,
        }),
      })

      const data = await response.json()

      if (data.token) {
        redemptionProperties = {
          _yotpo_coupon_code: data.reward_text,
          _swell_points_used: '1250',
          _swell_redemption_id: data.id.toString(),
          _swell_redemption_token: data.token,
          _swell_discount_type: 'fixed_amount',
          _swell_discount_amount: '7500',
          _swell_partial_redemption: 'true',
        }
      }
    } catch (error) {
      captureException(error)
    }
  }

  const programProperties = await getProgramProperties({
    selectedOptions,
    groupedAddonsWithQuantity,
    deliveryData,
    redemptionProperties,
    schedulePowWeek,
    selectedOptionsBeforeDowngrade,
  })

  const itemsToAdd: {
    merchandiseId: string
    quantity: number
    sellingPlanId?: string
    attributes?: { key: string; value: string }[]
  }[] = [
    {
      merchandiseId: selectedOptions.activeVariant.gid,
      quantity: 1,
      ...(selectedOptions.purchaseType === 'sub' && {
        sellingPlanId: `gid://shopify/SellingPlan/${MEAL_PROGRAM_SELLING_PLAN_ID}`,
      }),
      attributes:
        programProperties &&
        transformObjectToCartItemAttributes(programProperties),
    },
  ]

  if (Object.values(groupedAddonsWithQuantity).flat().length) {
    const addons = Object.values(groupedAddonsWithQuantity).flatMap(
      ({ addons }) => addons,
    )
    addons.forEach(({ addon, quantity }) => {
      itemsToAdd.push({
        merchandiseId: addon.variant.gid,
        quantity: quantity,
        ...(selectedOptions.purchaseType === 'sub' && {
          sellingPlanId: `gid://shopify/SellingPlan/${MEAL_PROGRAM_SELLING_PLAN_ID}`,
        }),
      })
    })
  }

  if (isInfoModalEnabled) {
    openDialog('program-info-modal')
  }
  const cart = await addItems(itemsToAdd)

  //TODO: open dialog
  window.dispatchEvent(
    new CustomEvent(NOTICE_ADD_TO_CART, {
      detail: {
        cart,
        item: selectedOptions.activeVariant,
        cartPopupItem: JSON.parse(
          JSON.stringify(selectedOptions.activeVariant),
        ),
        rawAddons: JSON.stringify(
          Object.values(groupedAddonsWithQuantity)
            .map((group) =>
              group.addons.map(({ addon, quantity }) => ({
                id: addon.variant.id,
                quantity,
              })),
            )
            .flat(),
        ),
        addonGroups: programProperties._addon_groups,
        ...(programProperties._start_date && {
          programStartDateString: programProperties._start_date,
        }),
      },
    }),
  )
  window.dispatchEvent(
    new CustomEvent(SHOW_CART_MODAL, { detail: { sectionId, sectionType } }),
  )

  setTimeout(() => {
    chord?.trackProductAdded({
      cart: cart,
      product: transformToChordItem(
        selectedOptions.activeVariant,
        null,
        groupedAddonsWithQuantity,
        selectedOptions.meals,
      ),
    })
  })
}
